import { supabase } from '../lib/supabase';

export type PracticePlanJobStatus = 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';

export type PracticePlanJob = {
  id: string;
  status: PracticePlanJobStatus;
  progress_total: number | null;
  progress_done: number | null;
  error: string | null;
  result: unknown | null;
};

export type PracticePlanJobRequest = {
  practiceId: string;
  teamId: string;
  request: unknown;
  /**
   * Caller-generated key (e.g. one `crypto.randomUUID()` per generate-button
   * press, before any async work) so a duplicate enqueue of the *same*
   * submission — a double-click that slips past the UI's own re-entrancy
   * guard, a retried request, a second tab — returns the existing job
   * instead of creating a second one. Omit for a genuinely new submission.
   */
  idempotencyKey?: string;
};

export const practicePlanJobService = {
  async enqueue(payload: PracticePlanJobRequest): Promise<{ jobId: string }> {
    const { data, error } = await supabase.functions.invoke('generate-practice-plan', {
      body: payload,
      headers: {},
      method: 'POST',
    } as any);

    if (error) throw error;
    if (!data?.job_id) throw new Error('Failed to enqueue practice plan job');
    return { jobId: data.job_id as string };
  },

  async run(jobId: string): Promise<PracticePlanJob> {
    const { data, error } = await supabase.functions.invoke('generate-practice-plan?action=run', {
      body: { jobId },
      headers: {},
      method: 'POST',
    } as any);

    if (error) throw error;
    if (!data?.job) throw new Error('Failed to run practice plan job');
    return data.job as PracticePlanJob;
  },

  async status(jobId: string): Promise<PracticePlanJob> {
    const { data, error } = await supabase.functions.invoke(`generate-practice-plan?action=status&jobId=${encodeURIComponent(jobId)}`, {
      body: {},
      headers: {},
      method: 'POST',
    } as any);

    // Some supabase-js versions treat GET-only function routes inconsistently; use fetch fallback if needed.
    if (!error && data?.job) return data.job as PracticePlanJob;

    const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-practice-plan?action=status&jobId=${encodeURIComponent(jobId)}`;
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token ?? ''}`,
      },
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(json?.error || 'Failed to fetch job status');
    if (!json?.job) throw new Error('Missing job payload');
    return json.job as PracticePlanJob;
  },
};

